/**
 * P6 — pure helpers for the agency domain-settings page.
 *
 * Kept side-effect-free so the FQDN validation and the polling start/stop
 * predicate can be unit-tested without React or the network (see
 * domain-helpers.test.ts).
 */
import type {
  AgencyDomain,
  AgencyDomainStatus,
} from '../../lib/api/client-reports/agency-domains-api';

/** Statuses that are still settling — the page polls while any custom domain is here. */
export const IN_FLIGHT_STATUSES: AgencyDomainStatus[] = [
  'pending_dns',
  'verifying',
];

/** Human label for each status chip. */
export const STATUS_LABEL: Record<AgencyDomainStatus, string> = {
  pending_dns: 'Pending DNS',
  verifying: 'Verifying',
  active: 'Active',
  failed: 'Failed',
  disabled: 'Disabled',
};

/** Chip modifier (maps to the client-reports__tag--* success/warning/danger styles). */
export function statusTone(
  status: AgencyDomainStatus
): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending_dns':
    case 'verifying':
      return 'warning';
    case 'failed':
      return 'danger';
    case 'disabled':
    default:
      return 'neutral';
  }
}

/**
 * Validate a hostname the user typed for a custom domain.
 *
 * Accepts a plausible FQDN: 2+ dot-separated labels, each 1–63 chars of
 * `[a-z0-9-]` not starting/ending with a hyphen, a TLD of 2+ letters, total
 * length ≤ 253. Case-insensitive; a single trailing dot is tolerated. Rejects
 * protocols, paths, ports, wildcards, spaces and bare hostnames (no TLD).
 *
 * This is a client-side sanity gate only — the backend remains authoritative.
 */
export function isValidFqdn(input: string): boolean {
  if (typeof input !== 'string') return false;
  const host = input.trim().replace(/\.$/, '').toLowerCase();
  if (host.length === 0 || host.length > 253) return false;
  // No scheme, path, port, whitespace, wildcard, or userinfo.
  if (/[\s/\\:*@?#]/.test(host)) return false;
  const labels = host.split('.');
  if (labels.length < 2) return false;
  const labelRe = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
  for (let i = 0; i < labels.length; i += 1) {
    const label = labels[i];
    if (!labelRe.test(label)) return false;
  }
  const tld = labels[labels.length - 1];
  if (!/^[a-z]{2,}$/.test(tld)) return false;
  return true;
}

/** Normalize a typed hostname (trim, lowercase, strip trailing dot + a leading scheme). */
export function normalizeHostname(input: string): string {
  return input
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .replace(/\.$/, '')
    .toLowerCase();
}

/**
 * Should the page keep polling GET agency-domains?
 *
 * Poll only while at least one CUSTOM domain is still settling (pending_dns or
 * verifying). The auto-provisioned subdomain never drives polling. Returns
 * false for an empty/loading list so we never spin on nothing.
 */
export function shouldPollDomains(domains: AgencyDomain[]): boolean {
  if (!Array.isArray(domains) || domains.length === 0) return false;
  return domains.some(
    (d) => d.type === 'custom' && IN_FLIGHT_STATUSES.includes(d.status)
  );
}

/** The primary subdomain row, if the backend has provisioned one. */
export function findPrimarySubdomain(
  domains: AgencyDomain[]
): AgencyDomain | undefined {
  return domains.find((d) => d.type === 'subdomain' && d.is_primary);
}
